import { useState } from 'react';

import { CheckCircle2, Send } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { ScrollArea } from '@/ui/base/scroll-area';
import { Textarea } from '@/ui/base/textarea';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';

import { rotuloDeSublinha } from '../apontamentos/sublinhas';

import type { PersonaDaTratativa, TratativaLinha } from './contrato';
import { useConversaDaTratativa } from './dados';
import { personaDoAutor, rotuloDaSituacao, SITUACAO_VARIANTE } from './situacao';

/**
 * A conversa de uma tratativa, aberta ao clicar numa linha da grade.
 *
 * O QUE MUDA EM RELAÇÃO AO PROTÓTIPO, e por quê:
 *
 * 1. `ResponsiveModal` no lugar de `Dialog` — a conversa precisa caber em
 *    375 px (CLAUDE.md, "Regras de interface", item 4), e no celular vira
 *    folha inferior em vez de caixa central espremida.
 * 2. "Responder" e "Marcar como resolvido" NÃO gravam. Não há rota de
 *    tratativas em `apps/api/src/modulos/`: os dois mostram o aviso honesto de
 *    indisponibilidade, no mesmo padrão de `SlaAprovacaoSmePage`. Decisão do
 *    dono de 2026-09-22 — renderizar o caminho inteiro é o que permite
 *    demonstrar a jornada à SMT/DTP; fingir que gravou, não.
 *
 * O QUE VEIO IGUAL: quem pode responder e quem pode resolver. Responder é da
 * contraparte (quem abriu não responde a si mesmo) e resolver é de quem abriu
 * — as duas regras existem no protótipo e no banco, e sumir com elas faria a
 * demonstração mostrar um fluxo que o sistema não terá.
 */

interface TratativaDetalheProps {
  tratativa: TratativaLinha;
  persona: PersonaDaTratativa;
  aberto: boolean;
  aoFechar: () => void;
}

export function TratativaDetalhe({ tratativa, persona, aberto, aoFechar }: TratativaDetalheProps) {
  const [rascunhos, setRascunhos] = useState<Record<string, string>>({});

  const conversa = useConversaDaTratativa(
    aberto
      ? {
          ordemServicoId: tratativa.ordemServicoId,
          ano: tratativa.ano,
          mes: tratativa.mes,
          sublinha: tratativa.sublinha,
        }
      : null,
  );

  const avisarIndisponivel = () => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);

  return (
    <ResponsiveModal
      open={aberto}
      onOpenChange={(estado) => {
        if (!estado) {
          aoFechar();
        }
      }}
      title="Tratativa de devolução"
      description={`OS ${tratativa.osNumero} · ${tratativa.credenciadoNome} · ${rotuloDeSublinha(tratativa.sublinha)}`}
      desktopMaxWidth="max-w-2xl"
    >
      {conversa.isLoading ? (
        <Spinner />
      ) : conversa.isError ? (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar a conversa desta tratativa"
          primaryAction={{ label: 'Tentar de novo', onClick: () => void conversa.refetch() }}
        />
      ) : (conversa.data ?? []).length === 0 ? (
        <EmptyState
          title="Nenhuma mensagem nesta tratativa"
          description="A conversa aparece aqui assim que a SME ou a DRE registrar a primeira observação."
        />
      ) : (
        <ScrollArea className="max-h-[55vh] pr-3">
          <ul className="space-y-4">
            {(conversa.data ?? []).map((comentario) => {
              const souAutor = personaDoAutor(comentario.autorPapel) === persona;
              const podeResponder = !souAutor && comentario.situacao !== 'RESOLVIDO';
              const podeResolver = souAutor && comentario.situacao !== 'RESOLVIDO';
              const campoResposta = `resposta-${comentario.id}`;

              return (
                <li
                  key={comentario.id}
                  className={cn(
                    'space-y-3 rounded-md border p-3 text-sm',
                    comentario.situacao === 'RESOLVIDO' ? 'bg-muted/30' : 'bg-card',
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge variant="outline">
                      {comentario.autorPapel} · {formatarDataHora(comentario.criadoEm)}
                    </Badge>
                    <Badge variant={SITUACAO_VARIANTE[comentario.situacao]}>
                      {rotuloDaSituacao(comentario.situacao, comentario.autorPapel, persona)}
                    </Badge>
                  </div>

                  <p className="whitespace-pre-wrap">{comentario.mensagem}</p>

                  {comentario.respostas.length > 0 && (
                    <ul className="space-y-2 border-l-2 border-border pl-3">
                      {comentario.respostas.map((resposta) => (
                        <li key={resposta.id} className="space-y-1">
                          <Badge variant="outline">
                            {resposta.autorPapel} · {formatarDataHora(resposta.criadoEm)}
                          </Badge>
                          <p className="whitespace-pre-wrap">{resposta.mensagem}</p>
                        </li>
                      ))}
                    </ul>
                  )}

                  {(podeResponder || podeResolver) && (
                    <div className="flex flex-col gap-2 pt-1">
                      {podeResponder && (
                        <div className="space-y-1">
                          <Label htmlFor={campoResposta}>Sua resposta</Label>
                          <div className="flex items-end gap-2">
                            <Textarea
                              id={campoResposta}
                              rows={2}
                              placeholder="Escreva a justificativa ou o encaminhamento"
                              value={rascunhos[comentario.id] ?? ''}
                              onChange={(evento) =>
                                setRascunhos((anterior) => ({
                                  ...anterior,
                                  [comentario.id]: evento.target.value,
                                }))
                              }
                            />
                            <Button
                              size="icon"
                              className="h-10 w-10 shrink-0"
                              disabled={(rascunhos[comentario.id]?.trim().length ?? 0) < 3}
                              onClick={avisarIndisponivel}
                              aria-label="Enviar resposta"
                            >
                              <Send className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                      )}

                      {podeResolver && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={avisarIndisponivel}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
                          Marcar como resolvido
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </ResponsiveModal>
  );
}
